import { useMemo } from 'react'

import { ArcadeModalVariant } from '@cash-design-system/react'
import { StatusResult } from '@cash-web/protos/squareup/franklin/status_result.pb'
import { BlockerType } from '@cash-web/shared-flow-data-access-context'
import { JourneyData, JourneyTarget } from '@cash-web/shared-journey-util-main'
import { isMobileDevice } from '@cash-web/shared-util-user-agent'

import { JourneyStore, useJourneyStoreSyncOnBlockerChange } from './JourneyStoreProvider'

const isMobile = isMobileDevice()
const targetOverride: Partial<Record<BlockerType, JourneyTarget>> = {
  card_customization: isMobile ? 'mobile-sheet' : 'fullscreen',
  card_tag_order_confirmation_blocker: isMobile ? 'mobile-sheet' : 'fullscreen',
  payment_device_customization_blocker: isMobile ? 'mobile-sheet' : 'fullscreen',
}

type JourneyModalVariant = Extract<ArcadeModalVariant, 'default' | 'fullscreen' | 'receipt'>
export const getModalVariant = (journey: JourneyData): JourneyModalVariant | undefined => {
  return journey.routeConfig?.modalVariant ?? (journey.opts?.['modalVariant'] as JourneyModalVariant | undefined)
}
export const getTarget = (journey: JourneyData, blockerType?: BlockerType, statusResult?: StatusResult) => {
  const modalVariant = getModalVariant(journey)
  const blocker = !blockerType && !statusResult ? journey.initialBlocker : blockerType

  let target =
    modalVariant === 'fullscreen' ? 'fullscreen' : (journey.routeConfig?.target ?? journey.opts?.scope?.target)
  if (blocker && targetOverride[blocker]) {
    target = targetOverride[blocker]
  }

  return target
}

export const getTargetHistory = (history: JourneyData[], store?: JourneyStore) => {
  const targetHistory: Partial<Record<JourneyTarget, JourneyData[]>> = {}

  for (const journey of history) {
    const storeInstance = store?.getSnapshot(journey.journeyId)
    const statusResult = storeInstance?.flow?.active?.statusResult
    const blockerType = storeInstance?.flow?.active?.blockerData?.type
    const target = getTarget(journey, blockerType, statusResult) ?? 'modal'
    targetHistory[target] ??= []
    targetHistory[target].push(journey)
  }

  return targetHistory
}

export const useTargetHistory = (history: JourneyData[]) => {
  const { store, syncValue } = useJourneyStoreSyncOnBlockerChange()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => getTargetHistory(history, store), [history, store, syncValue])
}
